/**
 * La latence de sortie d'un poste — de la remise d'une trame au décodeur à son
 * affichage —, tirée des relevés image par image.
 *
 * La médiane d'une fenêtre glissante : une image rendue en retard parce que le
 * fil principal était pris ne déplace rien, un changement durable de régime —
 * un onglet qui passe au rendu logiciel — se suit en une seconde. La valeur ne
 * change qu'au-delà d'un pas, pour ne pas renvoyer au worker un nouveau chiffre
 * à chaque image.
 */
export class OutputLatency {
  private readonly samples: number[] = [];
  private current: number;

  constructor(
    initialMs: number,
    private readonly windowSize = 30,
    private readonly minSamples = 5,
    private readonly stepMs = 5,
    private readonly maxSampleMs = 2_000,
  ) {
    this.current = initialMs;
  }

  get value(): number {
    return this.current;
  }

  get count(): number {
    return this.samples.length;
  }

  /**
   * Prend un relevé. Rend la nouvelle latence quand elle a assez changé pour
   * être transmise, `null` sinon.
   *
   * Un relevé négatif ou démesuré est écarté : une image vue sans remise
   * récente, ou un rendu interrompu, n'est pas une latence de sortie.
   */
  record(sampleMs: number): number | null {
    if (!Number.isFinite(sampleMs) || sampleMs < 0 || sampleMs > this.maxSampleMs) {
      return null;
    }

    this.samples.push(sampleMs);

    if (this.samples.length > this.windowSize) {
      this.samples.shift();
    }

    if (this.samples.length < this.minSamples) {
      return null;
    }

    const sorted = [...this.samples].sort((a, b) => a - b);
    const median = Math.round(sorted[Math.floor(sorted.length / 2)]);

    if (Math.abs(median - this.current) < this.stepMs) {
      return null;
    }

    this.current = median;

    return median;
  }
}
